// ============================================================
// UserAgentAccessTab.js
// Agent Access tab inside <UserDetailPage>. Rendering is
// 4-way conditional on the user's role:
//
//   admin    — full grants table + Grant New Module dialog +
//              revoke/re-grant flows against ClientUserAgents.
//   manager  — informational copy: full bundle by role.
//   carer    — informational copy: CareNotes only.
//   auditor  — informational copy: read-only evidence-layer
//              access + access expiry from the user record.
//
// Backend reference:
//   admin-users-entitlements-list.js (GET)
//   admin-users-entitlements-grant.js (POST { moduleKey })
//   admin-users-entitlements-revoke.js (DELETE)
//   admin-clients-modules-list.js (used to enumerate available
//     modules for the Grant dialog — filtered to globally + Client
//     active, minus the modules the user already has actively
//     granted).
// ============================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, AlertCircle, RotateCw, Plus, AlertTriangle } from 'lucide-react';
import Badge from '../components/Badge';
import ConfirmDialog from '../components/ConfirmDialog';
import Modal from '../components/Modal';
import CarePeopleRolePanel from '../components/CarePeopleRolePanel';
import CareChargeRolePanel from '../components/CareChargeRolePanel';
import CarePlanRolePanel from '../components/CarePlanRolePanel';
import CareRotaRolePanel from '../components/CareRotaRolePanel';
import NamedGrantsPanel from '../components/NamedGrantsPanel';
import { toast } from '../components/Toaster';
import {
  listAdminUserEntitlements,
  grantAdminUserEntitlement,
  revokeAdminUserEntitlement,
  listAdminClientModules,
} from '../api/adminClients';
import { translateError } from '../utils/errorMessages';
import { formatDate } from '../utils/format';
import StatusMessage from '../components/StatusMessage';
import './UserAgentAccessTab.css';

function isActiveGrant(g) {
  return g.revokedAt == null;
}

// The tab = the (role-conditional) Agent-grants section + the module-role
// panels (CarePeople; CareCharge — role-model expansion Push 1 2026-07-13).
// Each panel self-gates on the user's core role via its GET's
// coreRoleApplicable (ineligible roles see a "not applicable" note), so
// both are rendered unconditionally here.
export default function UserAgentAccessTab({ user, onChange }) {
  return (
    <>
      <EntitlementsSection user={user} onChange={onChange} />
      <CarePeopleRolePanel user={user} />
      <CareChargeRolePanel user={user} />
      <CarePlanRolePanel user={user} />
      <CareRotaRolePanel user={user} />
      <NamedGrantsPanel user={user} />
    </>
  );
}

function EntitlementsSection({ user, onChange }) {
  if (user.roleKey === 'manager') {
    return (
      <div className="user-agent-access__info">
        <p>
          This user is a Manager. They have access to all 8 AI agents
          (CareNotes + 7 CareIQ) automatically, based on their role.
          No individual grants are needed.
        </p>
      </div>
    );
  }

  if (user.roleKey === 'carer') {
    return (
      <div className="user-agent-access__info">
        <p>
          This user is a Carer. They have access to CareNotes only.
          No individual grants are needed.
        </p>
      </div>
    );
  }

  if (user.roleKey === 'auditor') {
    return (
      <div className="user-agent-access__info">
        <p>
          This user is an Auditor. Their access is read-only across
          evidence-layer modules (Oversight, CareAudit, CareTrack,
          CareComply, CareVoice).
        </p>
        {user.accessExpiresAt && (
          <p className="user-agent-access__info-meta">
            Access expires {formatDate(user.accessExpiresAt)}.
          </p>
        )}
      </div>
    );
  }

  return <AdminAgentAccess user={user} onChange={onChange} />;
}

function AdminAgentAccess({ user, onChange }) {
  const [entitlements, setEntitlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [allModules, setAllModules] = useState([]);

  const [grantOpen, setGrantOpen] = useState(false);
  const [grantSelection, setGrantSelection] = useState('');
  const [grantSubmitting, setGrantSubmitting] = useState(false);

  const [revokeTarget, setRevokeTarget] = useState(null); // { moduleKey, moduleName } | null
  const [revokeSubmitting, setRevokeSubmitting] = useState(false);

  const [pendingKey, setPendingKey] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listAdminUserEntitlements(user.clientUserId);
      setEntitlements(res.items || []);
    } catch (err) {
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  }, [user.clientUserId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Module catalogue fetched lazily — we need it for the Grant
  // dialog. Fail-soft: if it breaks, the dialog will show empty
  // and the user can dismiss; the table still works.
  useEffect(() => {
    let cancelled = false;
    listAdminClientModules(user.clientId)
      .then((res) => { if (!cancelled) setAllModules(res.items || []); })
      .catch(() => { /* dialog will show empty */ });
    return () => { cancelled = true; };
  }, [user.clientId]);

  const activeGrantKeys = useMemo(
    () => new Set(entitlements.filter(isActiveGrant).map((g) => g.moduleKey)),
    [entitlements]
  );

  const availableModules = useMemo(
    () => allModules.filter(
      (m) => m.isActiveGlobally && m.isActiveForClient && !activeGrantKeys.has(m.moduleKey)
    ),
    [allModules, activeGrantKeys]
  );

  function openGrantDialog() {
    setGrantSelection('');
    setGrantOpen(true);
  }

  async function handleGrantSubmit(e) {
    e.preventDefault();
    if (!grantSelection) return;
    setGrantSubmitting(true);
    try {
      await grantAdminUserEntitlement(user.clientUserId, grantSelection);
      toast.success('Module access granted');
      setGrantOpen(false);
      await fetchData();
      if (onChange) onChange();
    } catch (err) {
      toast.error(translateError(err));
    } finally {
      setGrantSubmitting(false);
    }
  }

  async function handleRevokeConfirm() {
    if (!revokeTarget) return;
    setRevokeSubmitting(true);
    try {
      await revokeAdminUserEntitlement(user.clientUserId, revokeTarget.moduleKey);
      toast.success('Module access revoked');
      setRevokeTarget(null);
      await fetchData();
    } catch (err) {
      toast.error(translateError(err));
    } finally {
      setRevokeSubmitting(false);
    }
  }

  async function handleRegrant(moduleKey) {
    setPendingKey(moduleKey);
    try {
      await grantAdminUserEntitlement(user.clientUserId, moduleKey);
      toast.success('Module access granted');
      await fetchData();
    } catch (err) {
      toast.error(translateError(err));
    } finally {
      setPendingKey(null);
    }
  }

  if (loading && entitlements.length === 0) {
    return (
      <StatusMessage variant="loading" className="user-agent-access__state">
        <Loader2 size={16} className="user-agent-access__spinner" />
        <span>Loading module access…</span>
      </StatusMessage>
    );
  }

  if (error) {
    return (
      <StatusMessage variant="error" className="user-agent-access__state user-agent-access__state--error">
        <AlertCircle size={16} />
        <span>{error}</span>
        <button type="button" className="user-agent-access__retry" onClick={fetchData}>
          <RotateCw size={14} />
          Retry
        </button>
      </StatusMessage>
    );
  }

  const isEmpty = entitlements.length === 0;
  const hasAvailable = availableModules.length > 0;

  return (
    <div className="user-agent-access">
      {user.requiresAgentSetup && (
        <div
          className="user-agent-access__setup-banner"
          role="status"
          data-testid="user-agent-access-setup-banner"
        >
          <AlertTriangle size={18} className="user-agent-access__setup-banner-icon" />
          <div className="user-agent-access__setup-banner-text">
            <strong className="user-agent-access__setup-banner-title">Setup required</strong>
            <p className="user-agent-access__setup-banner-body">
              This admin-role user has no modules granted. Until at least one module is granted,
              they cannot open any module and will see an empty dashboard on sign-in. Use the
              controls below to grant the modules this user needs.
            </p>
          </div>
        </div>
      )}

      <div className="user-agent-access__header">
        <h3 className="user-agent-access__heading">Module access</h3>
        <button
          type="button"
          className="user-agent-access__grant"
          onClick={openGrantDialog}
        >
          <Plus size={14} />
          Grant New Module
        </button>
      </div>
      <p className="user-agent-access__explainer" data-testid="user-agent-access-explainer">
        Each module granted here is what this admin can open: the module's records and pages,
        and its AI agent where it has one. A module that is not granted is closed to them, even
        through a direct link. Managers see every module your organisation subscribes to, and
        carers are not affected by these settings.
      </p>

      {isEmpty && (
        <StatusMessage variant="empty" className="user-agent-access__empty">
          <p>No module access granted yet.</p>
          <button
            type="button"
            className="user-agent-access__grant user-agent-access__grant--empty"
            onClick={openGrantDialog}
          >
            <Plus size={14} />
            Grant Module Access
          </button>
        </StatusMessage>
      )}

      {!isEmpty && (
        <ul className="user-agent-access__list" data-testid="user-agent-access-list">
          {entitlements.map((g) => {
            const active = isActiveGrant(g);
            const pending = pendingKey === g.moduleKey;
            return (
              <li key={g.moduleKey} className="user-agent-access__row">
                <div className="user-agent-access__info-cell">
                  <div className="user-agent-access__name-row">
                    <span className="user-agent-access__name">{g.moduleName}</span>
                    <Badge variant={active ? 'success' : 'neutral'}>
                      {active ? 'Active' : 'Revoked'}
                    </Badge>
                  </div>
                  <span className="user-agent-access__meta">
                    {active
                      ? <>Granted {formatDate(g.grantedAt)}{g.grantedBy && <> by {g.grantedBy}</>}</>
                      : <>Revoked {formatDate(g.revokedAt)}{g.revokedBy && <> by {g.revokedBy}</>}</>}
                  </span>
                </div>
                {active ? (
                  <button
                    type="button"
                    className="user-agent-access__row-action user-agent-access__row-action--danger"
                    onClick={() => setRevokeTarget({ moduleKey: g.moduleKey, moduleName: g.moduleName })}
                  >
                    Revoke
                  </button>
                ) : (
                  <button
                    type="button"
                    className="user-agent-access__row-action"
                    onClick={() => handleRegrant(g.moduleKey)}
                    disabled={pending}
                  >
                    {pending ? 'Re-granting…' : 'Re-grant'}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <Modal
        isOpen={grantOpen}
        onClose={() => { if (!grantSubmitting) setGrantOpen(false); }}
        title="Grant New Module"
        closeDisabled={grantSubmitting}
      >
        <form onSubmit={handleGrantSubmit} className="user-agent-access__form">
          {hasAvailable ? (
            <>
              <label className="user-agent-access__form-field">
                <span className="user-agent-access__form-label">Module</span>
                <select
                  value={grantSelection}
                  onChange={(e) => setGrantSelection(e.target.value)}
                  required
                  className="user-agent-access__form-select"
                >
                  <option value="" disabled>-- Select a module --</option>
                  {availableModules.map((m) => (
                    <option key={m.moduleKey} value={m.moduleKey}>{m.moduleName}</option>
                  ))}
                </select>
              </label>
              <div className="user-agent-access__form-actions">
                <button
                  type="button"
                  className="user-agent-access__cancel"
                  onClick={() => setGrantOpen(false)}
                  disabled={grantSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="user-agent-access__submit"
                  disabled={grantSubmitting || !grantSelection}
                >
                  {grantSubmitting ? 'Granting…' : 'Grant'}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="user-agent-access__form-empty">
                {user.email} already has access to all available agents for {user.clientName}.
              </p>
              <div className="user-agent-access__form-actions">
                <button
                  type="button"
                  className="user-agent-access__cancel"
                  onClick={() => setGrantOpen(false)}
                >
                  Close
                </button>
              </div>
            </>
          )}
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(revokeTarget)}
        onClose={() => { if (!revokeSubmitting) setRevokeTarget(null); }}
        onConfirm={handleRevokeConfirm}
        title={revokeTarget ? `Revoke ${revokeTarget.moduleName}?` : ''}
        message={
          revokeTarget
            ? `This user will lose access to ${revokeTarget.moduleName} immediately. They can be re-granted access later if needed.`
            : ''
        }
        confirmLabel="Revoke"
        isSubmitting={revokeSubmitting}
      />
    </div>
  );
}
